import { useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

import AIManagersService from 'src/services/AIManagersService';
import {
    canHandoff,
    ConversationHandoffError,
    isHumanControlled,
} from 'src/services/AIManagersService/conversationHandoff';

import type {
    AIManagerConversation,
    AIManagerHandoffResult,
    AIManagerConversationTranscript,
} from 'src/services/AIManagersService';

const FAILURE_TEXT = {
    preflight: 'Не удалось проверить состояние диалога. Передача не запускалась.',
    unavailable: 'Передача недоступна. Обновите диалог, чтобы проверить доступ.',
    changed: 'Состояние диалога изменилось. Обновите его перед действием.',
    rejected: 'Сервер отклонил передачу. Проверьте комментарий и повторите.',
    uncertain: 'Результат передачи пока не подтверждён. Проверьте состояние — повторно команду не отправляем.',
};

export default function ConversationHandoffAction({
    managerId,
    conversation,
    disabled,
    hasSending,
    onTransferred,
    onChecked,
    onUnavailable,
}: {
    managerId: string;
    conversation: AIManagerConversation;
    disabled: boolean;
    hasSending: boolean;
    onTransferred: (result: AIManagerHandoffResult) => void;
    onChecked: (result: AIManagerConversationTranscript) => void;
    onUnavailable: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [success, setSuccess] = useState(false);
    const [failure, setFailure] = useState<ConversationHandoffError['kind'] | null>(() =>
        canHandoff(conversation) && AIManagersService.handoffNeedsVerification(managerId, conversation.id)
            ? 'uncertain'
            : null
    );
    const alive = useRef(true);
    const saving = useRef(false);
    const resultRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
        };
    }, []);
    useEffect(() => {
        if (success) resultRef.current?.focus();
    }, [success]);

    const finish = (result: AIManagerHandoffResult) => {
        onTransferred(result);
        if (alive.current) {
            setSuccess(true);
            setFailure(null);
            setOpen(false);
        }
    };
    const transfer = async () => {
        if (saving.current || disabled) return;
        saving.current = true;
        setBusy(true);
        setFailure(null);
        try {
            finish(await AIManagersService.handoffConversation(managerId, conversation.id, note));
        } catch (error) {
            if (error instanceof ConversationHandoffError && error.kind === 'unavailable') onUnavailable();
            if (alive.current) setFailure(error instanceof ConversationHandoffError ? error.kind : 'uncertain');
        } finally {
            saving.current = false;
            if (alive.current) setBusy(false);
        }
    };
    const check = async () => {
        if (saving.current) return;
        saving.current = true;
        setBusy(true);
        try {
            const result = await AIManagersService.getConversationTranscript(managerId, conversation.id);
            if (isHumanControlled(result.conversation)) finish(result);
            else {
                onChecked(result);
                if (alive.current && failure !== 'uncertain') {
                    setFailure(canHandoff(result.conversation) ? null : 'changed');
                }
            }
        } catch (error) {
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 401 || status === 403 || status === 404) onUnavailable();
            // An inconclusive read must not re-enable a potentially duplicated write.
            if (alive.current) setFailure((previous) => (previous === 'uncertain' ? 'uncertain' : 'unavailable'));
        } finally {
            saving.current = false;
            if (alive.current) setBusy(false);
        }
    };

    if (success)
        return (
            <Box
                ref={resultRef}
                tabIndex={-1}
                role="status"
                sx={{ p: 2, borderTop: '1px solid var(--lm-border)' }}
            >
                <Typography variant="body2">Диалог передан человеку.</Typography>
            </Box>
        );
    if (!canHandoff(conversation) && !open) return null;
    const checkOnly = failure === 'uncertain' || failure === 'unavailable' || failure === 'changed';
    return (
        <>
            <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid var(--lm-border)', flexShrink: 0 }}>
                <Button
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    onClick={() => setOpen(true)}
                >
                    {failure === 'uncertain' ? 'Проверить передачу' : 'Передать человеку'}
                </Button>
            </Box>
            <Dialog
                open={open}
                onClose={() => {
                    if (!busy) setOpen(false);
                }}
                fullWidth
                maxWidth="xs"
                aria-labelledby="conversation-handoff-title"
                aria-describedby="conversation-handoff-description"
            >
                <DialogTitle id="conversation-handoff-title">Передать диалог человеку?</DialogTitle>
                <DialogContent>
                    <Typography
                        id="conversation-handoff-description"
                        variant="body2"
                        mb={2}
                    >
                        ИИ приостановит обработку этого диалога. Ожидающие ответы будут отменены.
                    </Typography>
                    {hasSending && (
                        <Typography
                            variant="body2"
                            color="secondary.main"
                            mb={2}
                        >
                            Уже начатая отправка может завершиться.
                        </Typography>
                    )}
                    <TextField
                        label="Комментарий для команды"
                        placeholder="Необязательно"
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={5}
                        inputProps={{ maxLength: 2000 }}
                        disabled={busy || checkOnly}
                    />
                    {failure && (
                        <Typography
                            role="alert"
                            variant="body2"
                            mt={2}
                            color="var(--lm-danger-ink)"
                        >
                            {FAILURE_TEXT[failure]}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        disabled={busy}
                        onClick={() => setOpen(false)}
                    >
                        Отмена
                    </Button>
                    {checkOnly ? (
                        <Button
                            variant="contained"
                            disabled={busy}
                            onClick={() => {
                                void check();
                            }}
                        >
                            {busy ? 'Проверяем…' : 'Проверить состояние'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={busy || disabled || !canHandoff(conversation)}
                            onClick={() => {
                                void transfer();
                            }}
                        >
                            {busy ? 'Передаём…' : 'Передать'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
